import { useState } from 'react'
import { useCandidates } from '../hooks/useCandidates'
import { useAuth } from '../contexts/AuthContext'
import CandidateCard from '../components/CandidateCard'
import { rankCandidates, loadUserAnswers, hasCompletedQuestionnaire } from '../engine/matchEngine'
import { ballotFor, describeBallot } from '../engine/ballotFilter'
import { loadDistrictKeys } from '../engine/districtSelection'
import { Link, useNavigate } from 'react-router-dom'

// All 50 US state codes in alphabetical order
const ALL_STATE_CODES = [
  'AK','AL','AR','AZ','CA','CO','CT','DE','FL','GA',
  'HI','IA','ID','IL','IN','KS','KY','LA','MA','MD',
  'ME','MI','MN','MO','MS','MT','NC','ND','NE','NH',
  'NJ','NM','NV','NY','OH','OK','OR','PA','RI','SC',
  'SD','TN','TX','UT','VA','VT','WA','WI','WV','WY'
]

export default function Candidates() {
  const { userPrefs } = useAuth()
  const navigate = useNavigate()

  const userState = userPrefs?.state || localStorage.getItem('civitas_state') || null
  // Written by the address lookup in onboarding. Empty until a voter has given
  // an address, which ballotFor reads as "show the whole state, and say so".
  const districtKeys = loadDistrictKeys()

  const [stateFilter, setStateFilter] = useState(userState || 'ALL')
  const [partyFilter, setPartyFilter] = useState('ALL')

  const { candidates, loading, error } = useCandidates(
    stateFilter !== 'ALL' ? { state: stateFilter } : {}
  )

  const completed = hasCompletedQuestionnaire()
  const { weights, stances } = loadUserAnswers()

  const ranked = completed && candidates.length
    ? rankCandidates(weights, stances, candidates)
    : candidates

  // Narrow to this voter's actual ballot before party filtering. Until now this
  // page filtered by state only, so a Virginia voter saw every Virginia
  // candidate rather than their own races - the gap #302 describes.
  //
  // ballotFor keeps statewide races (US Senator, Governor) that carry no
  // district, and sets aside candidates it cannot place instead of silently
  // dropping or silently including them. Both are reported to the voter below.
  const ballot = ballotFor(ranked, { state: stateFilter, districtKeys })
  const inScope = ballot.filtered ? [...ballot.onBallot, ...ballot.statewide] : ballot.unfiltered

  const filtered = inScope.filter(c => {
    if (partyFilter !== 'ALL' && c.party !== partyFilter) return false
    return true
  })

  // Build ordered state list: ALL first, then user's state, then rest alphabetically
  const orderedStates = [
    'ALL',
    ...(userState ? [userState] : []),
    ...ALL_STATE_CODES.filter(s => s !== userState)
  ]

  return (
    <div className="section">
      {/* Header row */}
      <div className="mb-4 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl text-civitas-navy mb-1">2026 Candidates</h1>
          <p className="text-civitas-silver text-sm">
            {/* When a ballot has been narrowed by address, say so and say what
                was set aside. A count that silently excludes candidates reads
                as complete when it is not. */}
            {ballot.filtered
              ? describeBallot(ballot)
              : stateFilter !== 'ALL'
                ? `Showing ${filtered.length} candidates in ${stateFilter}.`
                : `Showing all ${filtered.length} candidates.`
            }
            {completed
              ? ' Sorted by your match score.'
              : ''}
          </p>
          {!completed && (
            <Link to="/questionnaire" className="text-civitas-blue underline text-sm mt-0.5 inline-block">
              Take the questionnaire to see personalized match scores →
            </Link>
          )}
          {completed && userState && stateFilter === userState && (
            <p className="text-xs text-civitas-silver mt-0.5">
              Match scores are based on your questionnaire answers.
            </p>
          )}
        </div>

        {/* Change Location — real button, not tiny text link */}
        <button
          onClick={() => navigate('/questionnaire')}
          className="flex items-center gap-1.5 px-4 py-2 rounded-full border border-civitas-gold bg-civitas-gold/10 text-civitas-navy text-sm font-medium hover:bg-civitas-gold/20 transition-colors whitespace-nowrap"
        >
          📍 {userState ? `${userState} · Change location` : 'Set my location'}
        </button>
      </div>

      {/* Filter row */}
      <div className="mb-6 space-y-3">
        {/* State filter — all states in wrapped rows */}
        <div>
          <label className="text-xs font-semibold text-civitas-navy uppercase tracking-wide mb-2 block">Filter by State</label>
          <div className="flex flex-wrap gap-1.5">
            {orderedStates.map(s => (
              <button
                key={s}
                onClick={() => setStateFilter(s)}
                className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                  stateFilter === s
                    ? 'bg-civitas-navy text-white'
                    : s === userState
                    ? 'bg-amber-100 text-civitas-navy border border-amber-400 font-semibold'
                    : 'bg-gray-100 text-civitas-silver hover:bg-civitas-light hover:text-civitas-navy'
                }`}
              >
                {s === 'ALL' ? 'All States' : s}
                {s === userState && stateFilter !== userState ? ' ★' : ''}
              </button>
            ))}
          </div>
          {userState && (
            <p className="text-xs text-amber-600 mt-1">★ = your questionnaire state</p>
          )}
        </div>

        {/* Party filter */}
        <div className="flex items-center gap-2 flex-wrap">
          <label className="text-xs font-semibold text-civitas-navy uppercase tracking-wide">Party:</label>
          {['ALL', 'Democratic', 'Republican', 'Independent'].map(p => (
            <button
              key={p}
              onClick={() => setPartyFilter(p)}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                partyFilter === p
                  ? 'bg-civitas-navy text-white'
                  : 'bg-gray-100 text-civitas-silver hover:bg-civitas-light hover:text-civitas-navy'
              }`}
            >
              {p === 'ALL' ? 'All Parties' : p}
            </button>
          ))}
        </div>
      </div>

      {/* Candidate grid */}
      {loading && (
        <div className="text-center py-12 text-civitas-silver">
          <div className="animate-pulse text-lg">Loading candidates...</div>
        </div>
      )}

      {error && !loading && (
        <div className="text-center py-12 text-red-500">
          <p className="font-medium">Could not load candidates.</p>
          <p className="text-sm mt-1">{error}</p>
        </div>
      )}

      {!loading && !error && filtered.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map(c => <CandidateCard key={c.id} candidate={c} showMatch={completed} />)}
        </div>
      )}

      {!loading && !error && filtered.length === 0 && (
        <div className="text-center py-12 text-civitas-silver">
          <p>No candidates found for the selected filters.</p>
          {stateFilter !== 'ALL' && (
            <button
              onClick={() => setStateFilter('ALL')}
              className="mt-2 text-civitas-blue underline text-sm"
            >
              Show all states
            </button>
          )}
        </div>
      )}
    </div>
  )
}
